// src/lib/serialization.ts
/**
 * Scene Serialization (T-20)
 * 
 * Implements scene serialization for sharing and persistence.
 * Excludes transient UI state and compresses using gzip + base64.
 * 
 * Acceptance Criteria: Round-trip test equality (deep compare selective slices)
 */

// Removed LZString import - now using standard base64 encoding
import type { RootState } from '../store';
import type {
  ViewportState,
  NumberLineState,
  SecondaryLineState,
  JumpIndicatorsState,
  AnnotationsState,
  ConstantsState,
  EquivalentFractionsState,
  PreferencesState,
  LabelVisibilityState,
  CustomTicksState,
  MultiplierMarkerState,
  MeasurementObject,
} from '../types/redux';
import type { ExpressionsState, ExpressionObject } from '../store/slices/expressionsSlice';
import type { DNLToolsState } from '../types/dnl';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

/**
 * Schema version for backward compatibility
 * Increment when making breaking changes to the serialization format
 */
export const CURRENT_SCHEMA_VERSION = 1;

/**
 * Main export format for scene data
 */
export interface SceneExport {
  schemaVersion: number;
  timestamp: string;
  data: SerializableState;
}

/**
 * State that should be included in serialization
 * Excludes transient UI state (selections, loading indicators, etc.)
 */
export interface SerializableState {
  viewport: ViewportState;
  numberLines: NumberLineState;
  secondaryLine: SecondaryLineState;
  equivalentFractions: EquivalentFractionsState;
  expressions: CleanExpressionsState;
  jumpIndicators: JumpIndicatorsState;
  multipliers: MultiplierMarkerState;
  annotations: AnnotationsState;
  constants: ConstantsState;
  preferences: PreferencesState;
  labelVisibility: LabelVisibilityState;
  customTicks: CustomTicksState;
  measurements: MeasurementObject[];
  dnlTools: DNLToolsState;
}

/**
 * Cleaned expressions state without transient UI elements
 */
export interface CleanExpressionsState {
  objects: Array<Omit<ExpressionObject, 'isSelected'>>;
  environment: Record<string, any>;
  dependencies: Record<string, string[]>;
  isEnabled: boolean;
  nextId: number;
}

/**
 * Serialization options
 */
export interface SerializationOptions {
  includeTimestamp?: boolean;
  excludePreferences?: boolean;
  excludeAnnotations?: boolean;
}

// =============================================================================
// CORE SERIALIZATION FUNCTIONS
// =============================================================================

/**
 * Enhanced serialize function with circular reference detection
 */
export function serialize(
  state: RootState, 
  options: SerializationOptions = {}
): SceneExport {
  console.log('🔍 Starting serialization...');
  try {
    console.log('🔍 Selecting serializable state...');
    const serializableState = selectSerializableState(state, options);
    console.log('✅ Serializable state selected');
    
    // CRITICAL FIX: Test serialization before creating final result
    try {
      JSON.stringify(serializableState);
      console.log('✅ Serialization test passed');
    } catch (circularError) {
      console.error('❌ Circular reference detected in serializable state:', circularError);
      throw new Error(`Circular reference in state: ${circularError instanceof Error ? circularError.message : 'Unknown error'}`);
    }
    
    const result = {
      schemaVersion: CURRENT_SCHEMA_VERSION,
      timestamp: options.includeTimestamp !== false ? 
        new Date().toISOString() : 
        '1970-01-01T00:00:00.000Z',
      data: serializableState,
    };
    
    console.log('✅ Serialization completed successfully');
    return result;
  } catch (error) {
    console.error('❌ Serialization failed:', error);
    throw new Error(`Serialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Compress scene export to base64 string
 * Uses LZ-string compression for optimal size reduction
 */
export function compress(exportObj: SceneExport): string {
  try {
    const json = JSON.stringify(exportObj);
    
    // Use standard base64 encoding instead of LZString's custom encoding
    // to ensure compatibility with PHP's strict base64_decode validation
    const compressed = btoa(json);
    
    if (!compressed) {
      throw new Error('Compression returned empty result');
    }
    
    // Log compression stats for debugging
    const originalSize = new Blob([json]).size;
    const compressedSize = new Blob([compressed]).size;
    const ratio = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);
    
    console.debug(`Serialization: ${originalSize}B → ${compressedSize}B (${ratio}% reduction)`);
    
    return compressed;
  } catch (error) {
    console.error('Compression failed:', error);
    throw new Error(`Failed to compress scene data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Decompress base64 string back to scene export
 */
export function decompress(payload: string): SceneExport {
  try {
    if (!payload || typeof payload !== 'string') {
      throw new Error('Invalid payload: must be non-empty string');
    }
    
    // Use standard base64 decoding instead of LZString's custom decoding
    const decompressed = atob(payload);
    
    if (!decompressed) {
      throw new Error('Decompression failed - invalid or corrupted data');
    }
    
    const parsed = JSON.parse(decompressed);
    
    if (!isValidSceneExport(parsed)) {
      throw new Error('Invalid scene export format');
    }
    
    return parsed;
  } catch (error) {
    console.error('Decompression failed:', error);
    throw new Error(`Failed to decompress scene data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Hydrate scene export back to Redux state format
 * Prepares data for loading into the store
 */
export function hydrate(exportObj: SceneExport): Partial<RootState> & { measurements?: MeasurementObject[] } {
  try {
    validateSchema(exportObj);
    
    const { data } = exportObj;
    
    // Restore expression objects with default selection state
    const hydratedExpressions: ExpressionsState = {
      ...data.expressions,
      selectedObjectIds: [], // Reset selections
      widget: createDefaultWidget(), // Reset widget state
      objects: data.expressions.objects.map(obj => ({
        ...obj,
        isSelected: false, // Reset selection state
      })),
    };
    
    // Return partial state for Redux store hydration
    return {
      viewport: data.viewport,
      numberLines: data.numberLines,
      secondaryLine: data.secondaryLine,
    equivalentFractions: (() => {
      const fallbackDenominators = [2, 3, 4, 6, 12];
      const base = data.equivalentFractions || {
        mode: 'standard' as const,
        denominators: fallbackDenominators,
        activeDenominators: fallbackDenominators,
        integerLabelsOnly: true,
        labelFormat: 'fraction' as const,
      };
      const active = base.activeDenominators && base.activeDenominators.length > 0
        ? base.activeDenominators
        : base.denominators || fallbackDenominators;
      return {
        ...base,
        denominators: [...(base.denominators || fallbackDenominators)],
        activeDenominators: [...active],
      };
    })(),
      expressions: hydratedExpressions,
      jumpIndicators: data.jumpIndicators,
    multipliers: data.multipliers || { markers: [], nextId: 1 },
      annotations: data.annotations,
      constants: data.constants,
      preferences: data.preferences,
      labelVisibility: data.labelVisibility,
      customTicks: data.customTicks || { ticks: [], nextId: 1 },
      dnlTools: data.dnlTools || {
        sparseMode: { enabled: false, hideRegularTicks: true, customTicks: [], showZero: true, nextId: 1 },
        primaryLabel: { text: '', position: 'left', visible: false },
        secondaryLabel: { text: '', position: 'left', visible: false },
        primaryUnit: { text: '', position: 'after', enabled: false },
        secondaryUnit: { text: '', position: 'after', enabled: false },
      },
      // Measurements are returned separately since they're part of UI slice
      measurements: (data.measurements || []).map(m => ({ ...m, selected: false })),
      // Note: ui and share slices are intentionally omitted
      // They will use their default initial state
    };
  } catch (error) {
    console.error('Hydration failed:', error);
    throw new Error(`Failed to hydrate scene data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Select only the serializable parts of the Redux state
 */
function selectSerializableState(
  state: RootState, 
  options: SerializationOptions
): SerializableState {
  console.log('🔍 Extracting state slices...');
  const {
    viewport,
    numberLines,
    secondaryLine,
    equivalentFractions,
    expressions,
    jumpIndicators,
    multipliers,
    annotations,
    constants,
    preferences,
    labelVisibility,
    customTicks,
    dnlTools,
    ui,
  } = state;
  
  console.log('🔍 Cleaning expressions state...');
  const cleanedExpressions = cleanExpressionsForSerialization(expressions);
  console.log('✅ Expressions cleaned');
  
  // Extract measurements from UI state (remove selection state)
  const cleanedMeasurements = (ui?.measurement?.measurements || []).map(m => ({
    ...m,
    selected: false, // Reset selection on save
  }));
  
  console.log('🔍 Building result object...');
  const result: SerializableState = {
    viewport,
    numberLines,
    secondaryLine,
    equivalentFractions,
    expressions: cleanedExpressions,
    jumpIndicators,
    multipliers: multipliers || { markers: [], nextId: 1 },
    constants,
    labelVisibility,
    annotations: options.excludeAnnotations ? { shapes: [], texts: [], strokes: [], nextId: 1 } : annotations,
    preferences: options.excludePreferences ? createDefaultPreferences() : preferences,
    customTicks: customTicks || { ticks: [], nextId: 1 },
    measurements: cleanedMeasurements,
    dnlTools: dnlTools || {
      sparseMode: { enabled: false, hideRegularTicks: true, customTicks: [], showZero: true, nextId: 1 },
      primaryLabel: { text: '', position: 'left', visible: false },
      secondaryLabel: { text: '', position: 'left', visible: false },
      primaryUnit: { text: '', position: 'after', enabled: false },
      secondaryUnit: { text: '', position: 'after', enabled: false },
    },
  };
  
  console.log('✅ Result object built');
  return result;
}

/**
 * Clean expressions state by removing transient UI elements AND circular references
 */
function cleanExpressionsForSerialization(expressions: ExpressionsState): CleanExpressionsState {
  console.log('🔍 Cleaning expressions:', expressions.objects.length, 'objects');
  
  // CRITICAL FIX: Deep clean objects to remove any potential circular refs
  const cleanedObjects = expressions.objects.map(({ isSelected, ...obj }) => {
    // Create a clean copy that breaks any circular references
    return {
      id: obj.id,
      expression: obj.expression,
      position: obj.position,
      variables: [...obj.variables], // Create new array to break references
      style: { ...obj.style }, // Shallow copy style object
      createdAt: obj.createdAt,
      equivalentDenominator: obj.equivalentDenominator,
    };
  });
  
  console.log('✅ Objects cleaned');
  
  // CRITICAL FIX: Clean environment to remove circular references
  const cleanedEnvironment: Record<string, any> = {};
  for (const [key, value] of Object.entries(expressions.environment)) {
    try {
      // Test if this value can be serialized
      JSON.stringify(value);
      cleanedEnvironment[key] = value;
    } catch (error) {
      console.warn(`Skipping environment variable '${key}' due to serialization issue:`, error);
      // Skip variables that can't be serialized
    }
  }
  
  // CRITICAL FIX: Clean dependencies to ensure no circular refs
  const cleanedDependencies: Record<string, string[]> = {};
  for (const [objectId, deps] of Object.entries(expressions.dependencies)) {
    cleanedDependencies[objectId] = [...deps]; // Create new array
  }
  
  const result = {
    objects: cleanedObjects,
    environment: cleanedEnvironment,
    dependencies: cleanedDependencies,
    isEnabled: expressions.isEnabled,
    nextId: expressions.nextId,
  };
  
  console.log('✅ Expressions cleaning completed');
  return result;
}

/**
 * Create default widget state for expressions
 */
function createDefaultWidget(): ExpressionsState['widget'] {
  return {
    isVisible: false,
    position: { x: 0, y: 0 },
    mode: 'create',
    currentExpression: '',
    previewPosition: null,
  };
}

/**
 * Create default preferences state
 */
function createDefaultPreferences(): PreferencesState {
  return {
    theme: 'light',
    locale: 'en-GB',
    exportBackground: 'transparent',
    autoSave: true,
    keyboardShortcuts: true,
    accessibilityMode: false,
    constantsPrecision: 4,
    defaultScaleFactor: { n: 1, d: 2 },
    preferredOrientation: 'horizontal',
    showInstallPrompt: true,
    defaultJumpSize: 1,
    wheelReverseScroll: false,
    wheelScrollSensitivity: 1,
  };
}

/**
 * Validate that an object is a valid SceneExport
 */
function isValidSceneExport(obj: any): obj is SceneExport {
  return (
    obj &&
    typeof obj === 'object' &&
    typeof obj.schemaVersion === 'number' &&
    typeof obj.timestamp === 'string' &&
    obj.data &&
    typeof obj.data === 'object'
  );
}

/**
 * Validate schema version compatibility
 */
function validateSchema(exportObj: SceneExport): void {
  if (exportObj.schemaVersion !== CURRENT_SCHEMA_VERSION) {
    console.warn(`Schema version mismatch: expected ${CURRENT_SCHEMA_VERSION}, got ${exportObj.schemaVersion}`);
    
    if (exportObj.schemaVersion > CURRENT_SCHEMA_VERSION) {
      throw new Error(`Scene was created with a newer version of the app. Please update to load this scene.`);
    }
    
    // For older versions, we could implement migration logic here
    console.log('Loading scene from older version - attempting compatibility mode');
  }
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get the size of serialized data in different formats
 */
export function getSerializationStats(state: RootState): {
  originalSize: number;
  serializedSize: number;
  compressedSize: number;
  compressionRatio: number;
} {
  const exported = serialize(state);
  const json = JSON.stringify(exported);
  const compressed = compress(exported);
  
  const originalSize = JSON.stringify(state).length;
  const serializedSize = json.length;
  const compressedSize = compressed.length;
  const compressionRatio = ((serializedSize - compressedSize) / serializedSize * 100);
  
  return {
    originalSize,
    serializedSize,
    compressedSize,
    compressionRatio,
  };
}

/**
 * Validate that a round-trip operation preserves data integrity
 */
export function validateRoundTrip(state: RootState): boolean {
  try {
    const exported = serialize(state);
    const compressed = compress(exported);
    const decompressed = decompress(compressed);
    const hydrated = hydrate(decompressed);
    
    // Compare key slices (excluding transient state)
    const originalSerializable = selectSerializableState(state, {});
    const roundTripSerializable = selectSerializableState(
      { ...state, ...hydrated } as RootState, 
      {}
    );
    
    return JSON.stringify(originalSerializable) === JSON.stringify(roundTripSerializable);
  } catch (error) {
    console.error('Round-trip validation failed:', error);
    return false;
  }
}

/**
 * Check if the current state is compatible with serialization
 */
export function canSerialize(state: RootState): { canSerialize: boolean; issues: string[] } {
  const issues: string[] = [];
  
  try {
    // Check for circular references in expressions
    JSON.stringify(state.expressions);
  } catch (error) {
    issues.push('Expressions contain circular references');
  }
  
  try {
    // Check for oversized annotations
    const annotationsSize = JSON.stringify(state.annotations).length;
    if (annotationsSize > 1024 * 1024) { // 1MB limit
      issues.push('Annotations are too large (>1MB)');
    }
  } catch (error) {
    issues.push('Annotations contain invalid data');
  }
  
  // Check expression count
  if (state.expressions.objects.length > 500) {
    issues.push('Too many expression objects (>500)');
  }
  
  // Check jump indicators count
  if (state.jumpIndicators.jumps.length > 100) {
    issues.push('Too many jump indicators (>100)');
  }
  
  return {
    canSerialize: issues.length === 0,
    issues,
  };
}

/**
 * Enhanced JSON.stringify with timeout and circular reference detection
 */
function safeStringify(obj: any, timeout: number = 5000): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error('JSON.stringify timeout - likely circular reference'));
    }, timeout);
    
    try {
      // Use JSON.stringify with circular reference detection
      const cache = new Set();
      const result = JSON.stringify(obj, (key, value) => {
        if (typeof value === 'object' && value !== null) {
          if (cache.has(value)) {
            // Circular reference detected
            return '[Circular Reference Removed]';
          }
          cache.add(value);
        }
        return value;
      });
      
      clearTimeout(timeoutId);
      resolve(result);
    } catch (error) {
      clearTimeout(timeoutId);
      reject(error);
    }
  });
}